import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import apiUrl from "../Apiurl";
import { BlockSkeleton, TextSkeleton } from "../interfaccia/loading";
import "./PrestashopAlignmentPage.css";
import PrestashopAlignmentImageReview from "./PrestashopAlignmentImageReview";
import PrestashopAlignmentBatchPreview from "./PrestashopAlignmentBatchPreview";
import PrestashopAlignmentDashboard from "./PrestashopAlignmentDashboard";
import WebCategoryMappingCards from "./WebCategoryMappingCards";
import stilePulsanti from "../interfaccia/stilePulsanti";
import {
  actionLabel,
  actionMetadata,
  alignmentStatusLabel,
  asDisplayValue,
  countActionsByDomain,
  findingDetails,
  plannedActionStatus,
  semanticTextLabel,
  siteState,
  sourceComparison,
} from "./PrestashopAlignmentPage.helpers";

const DOMAIN_LABELS = {
  TEXT: "Testi",
  FEATURE: "Feature",
  CATEGORY: "Categorie",
  CATEGORY_DEFAULT: "Categoria predefinita",
  IMAGE: "Immagini",
  PRODUCT: "Prodotto",
};

const OPERATION_LABELS = {
  IMPORT_TO_DOMINO: "Scrive in Domino",
  QUEUE_TARGET_UPDATE: "Accoda l'aggiornamento Prestashop",
  CREATE_IMAGE_REVIEW: "Richiede revisione immagini",
};

const REVIEW_TABS = [
  { id: "summary", label: "Riepilogo", tracksChoices: false },
  { id: "texts", label: "Testi", tracksChoices: true },
  { id: "features", label: "Features", tracksChoices: true },
  { id: "categories", label: "Categorie", tracksChoices: true },
  { id: "images", label: "Immagini", tracksChoices: true },
  { id: "problems", label: "Problemi", tracksChoices: false },
];

const TEXT_REVIEW_SECTIONS = [
  {
    id: "title-short",
    label: "Titolo e desc breve",
    matches: (fieldPath) => fieldPath.includes("titolo") || fieldPath.includes("descBreve"),
  },
  {
    id: "intro",
    label: "Desc intro",
    matches: (fieldPath) => fieldPath.includes("descIntro"),
  },
  {
    id: "long",
    label: "Desc lunga",
    matches: (fieldPath) => fieldPath.includes("descLunga"),
  },
];

const LANGUAGE_LABELS = {
  it: "Italiano (IT)",
  en: "Inglese (EN)",
  fr: "Francese (FR)",
  de: "Tedesco (DE)",
  es: "Spagnolo (ES)",
};

const LANGUAGE_ORDER = ["it", "en", "fr", "de", "es"];

const TEXT_INFORMATION_FINDING_CODES = new Set([
  "MISSING_TRANSLATION_USING_IT_FALLBACK",
  "SOURCE_LANGUAGE_EQUALS_IT",
]);

function isInformationalTextFinding(finding) {
  return String(finding?.domain || "") === "TEXT" &&
    TEXT_INFORMATION_FINDING_CODES.has(String(finding?.code || ""));
}

function isFallbackTextAction(action) {
  return action?.domain === "TEXT" && actionMetadata(action).fallback === true;
}

const COMPLETED_REVIEW_STATUSES = new Set([
  "APPROVED", "APPLYING", "APPLIED", "SKIPPED", "CANCELLED",
]);

function formatDate(value) {
  if (!value) return "--";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("it-IT");
}

function statusTone(status) {
  if (["ERROR", "BLOCKED", "CONFLICT", "STALE"].includes(status)) return "error";
  if (["PARTIAL_READY", "PARTIAL_SUCCESS", "WAITING_DOWNSTREAM", "APPLYING"].includes(status)) return "warning";
  if (["READY", "APPROVED", "APPLIED", "SUCCESS"].includes(status)) return "ready";
  return "muted";
}

function actionStatus(action) {
  return String(action?.status || "PROPOSED");
}

function actionOperation(action) {
  return String(action?.action_type || action?.actionType || action?.operation || "");
}

function isDerivedText(action) {
  const metadata = actionMetadata(action);
  const planned = plannedActionStatus(action);
  return action?.domain === "TEXT" && Boolean(
    metadata.method ||
    metadata.derivationMethod ||
    ["READY_DERIVED", "NEEDS_EDITORIAL_REVIEW"].includes(planned),
  );
}

function isIntroAction(action) {
  return String(action?.field_path || action?.fieldPath || "").includes("descIntro");
}

function isLongDescriptionAction(action) {
  return String(action?.field_path || action?.fieldPath || "").includes("descLunga");
}

function isManualIntroAction(action) {
  const diagnostics = action?.diagnostics || {};
  const metadata = actionMetadata(action);
  const codes = diagnostics.diagnostics;
  return actionStatus(action) === "BLOCKED" &&
    action?.domain === "TEXT" &&
    actionOperation(action) === "QUEUE_TARGET_UPDATE" &&
    (action?.target_site || action?.targetSite) === "BOT" &&
    (action?.field_path || action?.fieldPath) === "descrizioniSuPS.descIntro" &&
    plannedActionStatus(action) === "BLOCKED" &&
    metadata.derivationMethod === "VIG_LONG_TO_BOT_INTRO" &&
    metadata.derivationConfidence === "LOW" &&
    Array.isArray(codes) && codes.length === 1 && codes[0] === "INTRO_SOURCE_UNSTRUCTURED" &&
    typeof action.sourceValue === "string" && Boolean(action.sourceValue.trim()) &&
    action.candidateValue == null;
}

function hasIntroText(value) {
  const document = new DOMParser().parseFromString(String(value || ""), "text/html");
  document.querySelectorAll("script,style,iframe,object,embed").forEach((node) => node.remove());
  return Boolean(document.body.textContent.trim());
}

function candidateString(action) {
  const value = action?.candidateValue;
  if (typeof value === "string") return value;
  return value === null || typeof value === "undefined" ? "" : JSON.stringify(value, null, 2);
}

function candidateChanged(action, draft) {
  return String(draft ?? "").trim() !== candidateString(action).trim();
}

function actionSelectable(action, drafts, reviewedIds = new Set()) {
  if (isManualIntroAction(action)) {
    return reviewedIds.has(Number(action.id)) && hasIntroText(drafts[String(action.id)]);
  }
  if (actionStatus(action) !== "PROPOSED") return false;
  if (!["IMPORT_TO_DOMINO", "QUEUE_TARGET_UPDATE"].includes(actionOperation(action))) return false;
  const planned = plannedActionStatus(action);
  if (["READY", "READY_DERIVED"].includes(planned)) return true;
  if (planned === "NEEDS_EDITORIAL_REVIEW" && isDerivedText(action)) {
    return reviewedIds.has(Number(action.id)) ||
      (!isIntroAction(action) && candidateChanged(action, drafts[String(action.id)]));
  }
  return false;
}

function approvalGroupKey(action) {
  const value = actionMetadata(action).approvalGroupKey;
  return typeof value === "string" && value ? value : null;
}

function groupedActionStatus(actions) {
  const statuses = actions.map(actionStatus);
  for (const status of ["ERROR", "CONFLICT", "STALE", "BLOCKED", "APPLYING", "APPROVED"]) {
    if (statuses.includes(status)) return status;
  }
  if (statuses.length && statuses.every((status) => ["APPLIED", "SKIPPED"].includes(status))) return "APPLIED";
  return statuses[0] || "PROPOSED";
}

function logicalReviewActions(actions) {
  const rows = (Array.isArray(actions) ? actions : []).filter((action) => !isFallbackTextAction(action));
  const groups = rows.reduce((result, action) => {
    const key = approvalGroupKey(action);
    if (key) {
      result[key] = result[key] || [];
      result[key].push(action);
    }
    return result;
  }, {});
  return rows.filter((action) => actionMetadata(action).approvalRole !== "PROPAGATION").map((action) => {
    const key = approvalGroupKey(action);
    const members = key ? groups[key] || [action] : [action];
    const propagation = members.find((member) => actionMetadata(member).approvalRole === "PROPAGATION");
    if (!propagation) return action;
    return {
      ...action,
      status: groupedActionStatus(members),
      downstream: propagation.downstream || action.downstream,
      approvalGroupActions: members,
      retryAction: members.find((member) => actionStatus(member) === "ERROR") || action,
    };
  });
}

function selectableReviewActionIds(actions, drafts, reviewedIds) {
  return logicalReviewActions(actions)
    .filter((action) => actionSelectable(action, drafts, reviewedIds))
    .map((action) => Number(action.id))
    .filter(Number.isFinite);
}

function reviewTabForAction(action) {
  if (action.domain === "TEXT") return "texts";
  if (action.domain === "FEATURE") return "features";
  if (String(action.domain || "").startsWith("CATEGORY")) return "categories";
  if (action.domain === "IMAGE") return "images";
  return "problems";
}

function actionReviewComplete(action, selectedIds) {
  return selectedIds.has(Number(action.id)) || COMPLETED_REVIEW_STATUSES.has(actionStatus(action));
}

function reviewProgress(tabId, actions, findings, selectedIds) {
  if (tabId === "problems") {
    const problemRows = Array.isArray(findings) ? findings : [];
    const problemActions = actions.filter((action) => reviewTabForAction(action) === "problems");
    return {
      done: problemRows.filter((finding) => String(finding.status || "OPEN") === "RESOLVED").length +
        problemActions.filter((action) => actionReviewComplete(action, selectedIds)).length,
      total: problemRows.length + problemActions.length,
    };
  }
  const rows = tabId === "summary"
    ? actions
    : actions.filter((action) => reviewTabForAction(action) === tabId);
  return {
    done: rows.filter((action) => actionReviewComplete(action, selectedIds)).length,
    total: rows.length,
  };
}

function ProgressIndicator({ progress, compact = false, tracksChoices = true }) {
  const showProgress = tracksChoices && progress.total > 0;
  const complete = showProgress && progress.done >= progress.total;
  const ariaLabel = showProgress
    ? progress.done + " di " + progress.total + " completate"
    : progress.total + " " + (progress.total === 1 ? "voce" : "voci");
  return (
    <span
      className={"alignment-progress-indicator" + (complete ? " alignment-progress-indicator--complete" : "") + (compact ? " alignment-progress-indicator--compact" : "")}
      aria-label={ariaLabel}
    >
      {complete ? <span aria-hidden="true">✓</span> : null}
      <span>{showProgress ? progress.done + "/" + progress.total : progress.total}</span>
    </span>
  );
}

function approvalTargetLabel(action) {
  return actionMetadata(action).propagateViaDomino === true
    ? "BOT → Domino → VIG"
    : (action.source_site || action.sourceSite || "--") + " → " + (action.target_site || action.targetSite || "Domino");
}

function CategoryListPreview({ value }) {
  const associated = Array.isArray(value?.associated) ? value.associated : [];
  const shown = associated.slice(0, 3);
  const defaultCategory = value?.default;
  if (!associated.length && !defaultCategory) return <span className="scrittaColMedia">—</span>;
  return (
    <div className="alignment-list-category-preview">
      {value?.site ? <strong className="db scrittaColMedia mb1">{value.site}</strong> : null}
      {shown.map((category) => (
        <div key={category.id} className="alignment-list-category-preview__entry" title={category.path}>
          {category.path}
          {Number(category.id) === Number(defaultCategory?.id) ? <span className="alignment-list-category-preview__default">predef.</span> : null}
        </div>
      ))}
      {associated.length > shown.length ? (
        <div className="scrittaColMedia" title={associated.slice(shown.length).map((category) => category.path).join("\n")}>
          +{associated.length - shown.length} altre
        </div>
      ) : null}
      {defaultCategory && !shown.some((category) => Number(category.id) === Number(defaultCategory.id)) ? (
        <div className="alignment-list-category-preview__entry" title={defaultCategory.path}>
          {defaultCategory.path}<span className="alignment-list-category-preview__default">predef.</span>
        </div>
      ) : null}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="pa3 overflow-auto" aria-hidden="true">
      {Array.from({ length: 7 }).map((_, index) => (
        <div key={index} className="flex items-center pv2 bb bordoColAlt" style={{ gap: "0.75rem" }}>
          <BlockSkeleton tone="soft" width="18%" height={18} />
          <BlockSkeleton tone="soft" width="17%" height={18} />
          <BlockSkeleton tone="soft" width="22%" height={18} />
          <BlockSkeleton tone="soft" width="15%" height={18} />
          <BlockSkeleton tone="soft" width="20%" height={18} />
          <BlockSkeleton tone="soft" width="20%" height={18} />
          <BlockSkeleton tone="soft" width="18%" height={18} />
        </div>
      ))}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="pa3" aria-hidden="true">
      <TextSkeleton tone="soft" width="44%" lines={1} />
      <div className="mt3"><BlockSkeleton tone="soft" height={90} /></div>
      <div className="mt3"><BlockSkeleton tone="soft" height={140} /></div>
      <div className="mt3"><BlockSkeleton tone="soft" height={100} /></div>
    </div>
  );
}

function Badge({ children, tone = "muted" }) {
  return <span className={`alignment-badge alignment-badge--${tone}`}>{children}</span>;
}

function EmptyInspector() {
  return (
    <div className="alignment-empty pa4 scrittaColMedia">
      <div>
        <p className="ma0 f5">Seleziona un articolo</p>
        <p className="mt2 mb0 f6">Apri il confronto per revisionare e confermare le singole azioni.</p>
      </div>
    </div>
  );
}

function htmlPreviewDocument(html) {
  return "<!doctype html><html><head><meta charset=\"utf-8\"><style>body{margin:0;padding:1rem;font:14px/1.55 Arial,sans-serif;color:#111}img{max-width:100%;height:auto}table{max-width:100%;border-collapse:collapse}td,th{padding:.35rem;border:1px solid #ddd}</style></head><body>" + html + "</body></html>";
}

function isHtmlValue(value) {
  return typeof value === "string" && /<\/?[a-z][\s\S]*>/i.test(value);
}

function ActionValue({ title, value, open = false, collapsible = true }) {
  const html = isHtmlValue(value);
  const content = (
    <>
      {html ? <iframe className="alignment-html-preview mt2" sandbox="" title={title + " - anteprima HTML"} srcDoc={htmlPreviewDocument(value)} /> : null}
      <pre className="alignment-value mt2 pa2 sfondoColChiarissimo">{asDisplayValue(value)}</pre>
    </>
  );
  if (!collapsible) {
    return (
      <div className="alignment-value-block mt2">
        <div className="f7 b">{title}</div>
        {content}
      </div>
    );
  }
  return (
    <details className="mt2" open={open}>
      <summary className="pointer f7">{title}</summary>
      {content}
    </details>
  );
}

function featureDescriptions(features) {
  return (Array.isArray(features) ? features : []).flatMap((feature) => {
    const values = Array.isArray(feature?.values) ? feature.values : [];
    const italian = values.filter((row) => String(row?.isoCode || "").toLowerCase() === "it" && row?.value);
    const selected = italian.length ? italian : values.filter((row) => row?.value);
    return selected.map((row) => row.value);
  });
}

function featureDescriptionsForAction(action, snapshot) {
  const metadata = actionMetadata(action);
  if (Array.isArray(metadata.sourceValues) && metadata.sourceValues.length) return metadata.sourceValues;
  const sourceSite = action.source_site || action.sourceSite;
  const sourceIds = new Set((metadata.sourceFeatureIds || []).map(String));
  const features = snapshot?.sites?.[sourceSite]?.features || [];
  return featureDescriptions((Array.isArray(features) ? features : []).filter((feature) => sourceIds.has(String(feature?.featureId))));
}

function FeatureSnapshotSummary({ features }) {
  if (!Array.isArray(features) || !features.length) return null;
  return (
    <details className="mt2">
      <summary className="pointer f7">Feature rilevate</summary>
      <ul className="alignment-feature-list f7 mt2 mb0 pl3">
        {features.map((feature, index) => {
          const descriptions = featureDescriptions([feature]);
          const names = (Array.isArray(feature?.names) ? feature.names : []).filter((row) => row?.value);
          const italianName = names.find((row) => String(row?.isoCode || "").toLowerCase() === "it");
          const featureName = italianName?.value || names[0]?.value || feature?.mappedPath || "Feature";
          const key = String(feature?.mappedPath || feature?.featureId || "feature") + "-" + index;
          return <li key={key}><strong>{featureName}</strong>{descriptions.length ? ": " + descriptions.join(" - ") : ""}</li>;
        })}
      </ul>
    </details>
  );
}

function categoryIdsFromDisplayValue(value) {
  const ids = [];
  const visit = (entry) => {
    if (Array.isArray(entry)) { entry.forEach(visit); return; }
    if (entry && typeof entry === "object") {
      const direct = entry.categoryId ?? entry.externalId ?? entry.external_id ?? entry.id;
      if (typeof direct !== "undefined") { visit(direct); return; }
      ["associatedIds", "categoryIds", "categories", "defaultId", "defaultCategoryId"].forEach((key) => {
        if (typeof entry[key] !== "undefined") visit(entry[key]);
      });
      return;
    }
    const id = Number(entry);
    if (Number.isFinite(id) && id > 0) ids.push(id);
  };
  visit(value);
  return Array.from(new Set(ids));
}

function CategoryDescriptions({ site, values, descriptions, title = "Categorie rilevate" }) {
  const normalizedSite = String(site || "").toUpperCase();
  const ids = categoryIdsFromDisplayValue(values);
  if (!ids.length || !["VIG", "BOT"].includes(normalizedSite)) return null;
  const byId = descriptions?.[normalizedSite] || {};
  return (
    <div className="mt2">
      <div className="f7 b">{title} ({normalizedSite})</div>
      <ul className="alignment-feature-list f7 mt1 mb0 pl3">
        {ids.map((id) => {
          const description = byId[String(id)] || {};
          const path = description.path || description.label || normalizedSite + " #" + id;
          return <li key={normalizedSite + "-" + id}><strong>{path}</strong> <span className="scrittaColMedia">- ID {id}</span></li>;
        })}
      </ul>
    </div>
  );
}

function SourceComparisonPanel({ snapshot, sourceName, productId, categoryDescriptions }) {
  const comparison = sourceComparison(snapshot, sourceName);
  const current = sourceName === "DOMINO"
    ? { label: snapshot?.domino ? "Disponibile" : "Non rilevato", tone: snapshot?.domino ? "ready" : "muted" }
    : siteState(snapshot, sourceName);
  return (
    <div className="alignment-panel pa2">
      <div className="flex justify-between items-center mb2" style={{ gap: "0.35rem" }}>
        <strong>{sourceName === "DOMINO" ? "Domino" : sourceName}</strong>
        <Badge tone={current.tone}>{current.label}</Badge>
      </div>
      <div className="f7 lh-copy">ID prodotto: {productId || "--"}</div>
      <div className="alignment-source-stats mt2 f7">
        <span>Testi <strong>{comparison.counts.texts}</strong></span>
        <span>Feature <strong>{comparison.counts.features}</strong></span>
        <span>Categorie <strong>{comparison.counts.categories}</strong></span>
        <span>Immagini <strong>{comparison.counts.images}</strong></span>
      </div>
      <FeatureSnapshotSummary features={comparison.features} />
      {sourceName === "DOMINO" ? (
        <>
          <CategoryDescriptions site="VIG" values={[comparison.categories?.associatedVig, comparison.categories?.defaultVig]} descriptions={categoryDescriptions} />
          <CategoryDescriptions site="BOT" values={[comparison.categories?.associatedBot, comparison.categories?.defaultBot]} descriptions={categoryDescriptions} />
        </>
      ) : <CategoryDescriptions site={sourceName} values={comparison.categories} descriptions={categoryDescriptions} />}
      <ActionValue
        title={`Dati ${sourceName === "DOMINO" ? "Domino" : sourceName} preservati`}
        value={{
          testi: comparison.texts,
          categorie: comparison.categories,
          immagini: comparison.images,
          diagnostica: comparison.diagnostics,
        }}
      />
    </div>
  );
}

function DownstreamState({ downstream }) {
  if (!downstream) return null;
  const done = Number(downstream.success) === 1;
  const failed = Number(downstream.error) === 1;
  const pending = Number(downstream.toSync) === 1;
  const label = done ? "Prestashop completato" : failed ? "Prestashop in errore" : pending ? "In coda Prestashop" : "Esito downstream da riconciliare";
  return (
    <div className="alignment-downstream mt2 pa2">
      <div className="flex flex-wrap items-center" style={{ gap: "0.4rem" }}>
        <Badge tone={failed ? "error" : pending ? "warning" : done ? "ready" : "muted"}>{label}</Badge>
        <span className="f7">Dashboard #{downstream.dashboardId} - {downstream.fonte || "--"} - prodotto {downstream.codPS || "--"}</span>
      </div>
      {downstream.response ? <ActionValue title="Risposta downstream" value={downstream.response} /> : null}
    </div>
  );
}

function IntroEditorDialog({ action, value, onCancel, onConfirm }) {
  const [editorValue, setEditorValue] = useState(value);
  const manualIntro = isManualIntroAction(action);
  return createPortal(
    <div className="alignment-dialog-backdrop alignment-intro-editor-backdrop">
      <div className="alignment-dialog alignment-intro-editor-dialog pa3" role="dialog" aria-modal="true" aria-label={`Revisiona descIntro ${actionLabel(action)}`}>
        <h2 className="f4 mt0">{manualIntro ? "Scrivi descIntro" : "Revisiona descIntro"}</h2>
        <p className="f6 lh-copy">{manualIntro
          ? "La descrizione lunga non contiene sezioni VANTAGGI o CARATTERISTICHE riconoscibili. Scrivi un testo HTML per Bottero, senza titolo; sarà approvato solo dopo la tua conferma."
          : "Controlla la proposta nel contesto del prodotto. Puoi confermarla così com'è oppure correggerla; la revisione resta manuale in entrambi i casi."}</p>
        <SunEditor
          setContents={value}
          onChange={setEditorValue}
          height="320px"
          setOptions={{
            buttonList: [
              ["undo", "redo"],
              ["formatBlock", "bold"],
              ["list"],
              ["codeView"],
            ],
            formats: ["p", "h2", "h3", "h4"],
            defaultStyle: "font-family: 'Roboto', Arial, sans-serif;",
          }}
        />
        <div className="flex justify-end mt3" style={{ gap: "0.5rem" }}>
          <button type="button" className="alignment-button alignment-button--secondary" onClick={onCancel}>Annulla</button>
          <button type="button" className="alignment-button" disabled={!hasIntroText(editorValue)} onClick={() => onConfirm(editorValue)}>Conferma e seleziona</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DerivedTextEditor({ action, value, onChange, onConfirmIntro, reviewed, disabled }) {
  const intro = isIntroAction(action);
  const longDescription = isLongDescriptionAction(action);
  const html = intro || longDescription;
  const medium = plannedActionStatus(action) === "NEEDS_EDITORIAL_REVIEW";
  const manualIntro = isManualIntroAction(action);
  const [open, setOpen] = useState(false);
  return (
    <div className="alignment-editor mt3 pa3">
      <div className="flex justify-between items-center" style={{ gap: "0.5rem" }}>
        <strong className="f7">{intro ? "Editor HTML descIntro" : longDescription ? "Editor HTML descrizione lunga" : "Editor testo derivato"}</strong>
        {medium || manualIntro ? <Badge tone="warning">{manualIntro ? "Testo manuale richiesto" : "Revisione obbligatoria"}</Badge> : <Badge>Modifica facoltativa</Badge>}
      </div>
      {intro ? (
        <div className="mt2 flex flex-wrap items-center" style={{ gap: "0.5rem" }}>
          <button type="button" className="alignment-button alignment-button--secondary" disabled={disabled} onClick={() => setOpen(true)}>
            {manualIntro ? "Scrivi descIntro" : "Apri editor descIntro"}
          </button>
          {reviewed ? <Badge tone="ready">Revisione confermata</Badge> : null}
          {open ? (
            <IntroEditorDialog
              action={action}
              value={value}
              onCancel={() => setOpen(false)}
              onConfirm={(nextValue) => {
                onConfirmIntro(nextValue);
                setOpen(false);
              }}
            />
          ) : null}
        </div>
      ) : (
        <label className="db mt2 f7">
          <span className="db mb1">Candidato da approvare</span>
          <textarea
            className="alignment-editor-input w-100 pa2"
            aria-label={`Modifica candidato ${actionLabel(action)}`}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            disabled={disabled}
            rows={html ? 12 : 4}
            maxLength={html ? undefined : 255}
          />
        </label>
      )}
      {!html ? <div className="tr f7 scrittaColMedia">{String(value || "").length}/255 caratteri</div> : null}
      {html ? (
        <div className="mt2">
          <div className="f7 mb1">Anteprima HTML elaborato</div>
          <iframe
            className="alignment-html-preview"
            sandbox=""
            title={`Anteprima candidato ${actionLabel(action)}`}
            srcDoc={htmlPreviewDocument(value)}
          />
        </div>
      ) : null}
      {(medium || manualIntro) && !reviewed && (intro || !candidateChanged(action, value)) ? (
        <p className="f7 mb0 alignment-editor-warning">
          {manualIntro ? "Non esiste una proposta automatica: scrivi la descIntro nell'editor e confermala per selezionarla." : intro ? "Apri l'editor e conferma la proposta, anche senza modifiche, per selezionarla." : "Modifica il candidato per renderlo selezionabile: la confidenza media non consente approvazione automatica."}
        </p>
      ) : null}
    </div>
  );
}

function textActionFieldPath(action) {
  return String(action?.field_path || action?.fieldPath || "");
}

function textActionLanguage(action) {
  return String(action?.language_iso || action?.language || "n/d").toLowerCase();
}

function textFindingFieldPath(finding) {
  return String(finding?.field_path || finding?.fieldPath || "");
}

function textFindingLanguage(finding) {
  const details = findingDetails(finding);
  return String(finding?.language_iso || finding?.language || details.redundantLanguage || "n/d").toLowerCase();
}

function TextInformationNotice({ finding }) {
  const language = textFindingLanguage(finding);
  const languageLabel = LANGUAGE_LABELS[language] || language.toUpperCase();
  const redundant = finding.code === "SOURCE_LANGUAGE_EQUALS_IT";
  return (
    <article className="alignment-notice ma3 pa3" role="note">
      <div className="flex flex-wrap items-center" style={{ gap: "0.4rem" }}>
        <Badge tone="warning">Non proposto</Badge>
        <strong className="f7">{semanticTextLabel(textFindingFieldPath(finding))} · {languageLabel}</strong>
      </div>
      <p className="f7 lh-copy mb0 mt2">
        {redundant
          ? "Il contenuto " + languageLabel + " coincide con quello italiano ed è stato escluso dall’allineamento."
          : "La traduzione " + languageLabel + " è assente e l’unico contenuto disponibile è quello italiano: nessun testo viene proposto."}
      </p>
    </article>
  );
}

function compareTextLanguages(left, right) {
  const leftIndex = LANGUAGE_ORDER.indexOf(left);
  const rightIndex = LANGUAGE_ORDER.indexOf(right);
  const leftRank = leftIndex === -1 ? LANGUAGE_ORDER.length : leftIndex;
  const rightRank = rightIndex === -1 ? LANGUAGE_ORDER.length : rightIndex;
  return leftRank - rightRank || left.localeCompare(right);
}

function TextReviewSections({ actions, findings = [], renderAction }) {
  const [activeSectionId, setActiveSectionId] = useState(TEXT_REVIEW_SECTIONS[0].id);
  const [activeLanguage, setActiveLanguage] = useState("");
  const activeSection = TEXT_REVIEW_SECTIONS.find(
    (section) => section.id === activeSectionId,
  ) || TEXT_REVIEW_SECTIONS[0];
  const sectionActions = actions.filter((action) =>
    activeSection.matches(textActionFieldPath(action)),
  );
  const sectionFindings = findings.filter((finding) =>
    activeSection.matches(textFindingFieldPath(finding)),
  );
  const languages = Array.from(
    new Set(sectionActions.map(textActionLanguage).concat(sectionFindings.map(textFindingLanguage))),
  ).sort(compareTextLanguages);
  const resolvedLanguage = languages.includes(activeLanguage)
    ? activeLanguage
    : languages[0] || "";
  const visibleActions = sectionActions.filter(
    (action) => textActionLanguage(action) === resolvedLanguage,
  );
  const visibleFindings = sectionFindings.filter(
    (finding) => textFindingLanguage(finding) === resolvedLanguage,
  );
  const panelId = "alignment-text-panel-" + activeSection.id;
  const activeLanguageLabel = LANGUAGE_LABELS[resolvedLanguage] ||
    resolvedLanguage.toUpperCase();

  useEffect(() => {
    if (activeLanguage !== resolvedLanguage) {
      setActiveLanguage(resolvedLanguage);
    }
  }, [activeLanguage, resolvedLanguage]);

  return (
    <div className="alignment-text-pages">
      <nav
        className="alignment-text-type-tabs"
        role="tablist"
        aria-label="Tipo di testo"
      >
        {TEXT_REVIEW_SECTIONS.map((section) => {
          const active = section.id === activeSection.id;
          return (
            <button
              key={section.id}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={"alignment-text-panel-" + section.id}
              className={"alignment-text-type-tab" +
                (active ? " alignment-text-type-tab--active" : "")}
              onClick={() => {
                setActiveSectionId(section.id);
                setActiveLanguage("");
              }}
            >
              {section.label}
            </button>
          );
        })}
      </nav>

      {languages.length ? (
        <nav
          className="alignment-text-language-tabs"
          role="tablist"
          aria-label={"Lingue " + activeSection.label}
        >
          {languages.map((language) => {
            const active = language === resolvedLanguage;
            return (
              <button
                key={language}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={panelId}
                className={"alignment-text-language-tab" +
                  (active ? " alignment-text-language-tab--active" : "")}
                onClick={() => setActiveLanguage(language)}
              >
                {LANGUAGE_LABELS[language] || language.toUpperCase()}
              </button>
            );
          })}
        </nav>
      ) : null}

      <section
        className="alignment-text-page"
        id={panelId}
        role="tabpanel"
        aria-label={activeSection.label +
          (activeLanguageLabel ? " - " + activeLanguageLabel : "")}
      >
        <header className="alignment-text-page-heading">
          <h3 className="ma0 f4">{activeSection.label}</h3>
          {activeLanguageLabel ? <h4 className="ma0 f6">{activeLanguageLabel}</h4> : null}
        </header>
        {visibleFindings.length ? (
          <div className="alignment-text-information">
            {visibleFindings.map((finding) => (
              <TextInformationNotice
                key={finding.id || [finding.code, textFindingFieldPath(finding), textFindingLanguage(finding)].join("-")}
                finding={finding}
              />
            ))}
          </div>
        ) : null}
        {visibleActions.length ? (
          <div className="alignment-tab-actions">
            {visibleActions.map(renderAction)}
          </div>
        ) : !visibleFindings.length ? (
          <p className="ma0 pa3 f7 scrittaColMedia">
            Nessun testo da revisionare in questa sezione.
          </p>
        ) : null}
      </section>
    </div>
  );
}

function ActionRow({
  action,
  snapshot,
  categoryDescriptions,
  selected,
  drafts,
  reviewedIds,
  canWrite,
  busy,
  onToggle,
  onDraftChange,
  onConfirmIntro,
  onRetry,
  itemId,
  sourceProductId,
  token,
  onRefresh,
  onReconcile,
}) {
  const metadata = actionMetadata(action);
  const fallback = metadata.fallback === true;
  const method = metadata.method || metadata.derivationMethod;
  const confidence = metadata.confidence || metadata.derivationConfidence;
  const plannedStatus = plannedActionStatus(action);
  const isText = action.domain === "TEXT";
  const isFeature = action.domain === "FEATURE";
  const isCategory = String(action.domain || "").startsWith("CATEGORY");
  const categorySite = action.target_site || action.targetSite || action.source_site || action.sourceSite;
  const isImage = action.domain === "IMAGE" && actionOperation(action) === "CREATE_IMAGE_REVIEW";
  const featureValues = isFeature ? featureDescriptionsForAction(action, snapshot) : [];
  const selectable = actionSelectable(action, drafts, reviewedIds);
  const currentStatus = actionStatus(action);
  const derived = isDerivedText(action);
  const draft = drafts[String(action.id)] ?? candidateString(action);
  const disabledReason = isManualIntroAction(action) && !selectable
      ? "Scrivi e conferma la descIntro nell'editor prima di selezionarla."
      : plannedStatus === "NEEDS_EDITORIAL_REVIEW" && !selectable
      ? isIntroAction(action) ? "Conferma il candidato nell'editor prima di selezionarlo." : "Correggere il candidato prima di selezionarlo."
      : !selectable && !["READY", "READY_DERIVED"].includes(plannedStatus)
        ? "Azione bloccata o non approvabile."
        : null;

  return (
    <article className={`alignment-action pv3 ${selected ? "alignment-action--selected" : ""}`}>
      <div className="alignment-action-heading">
        <label className="alignment-action-check">
          <input
            type="checkbox"
            aria-label={`Seleziona ${actionLabel(action)}`}
            checked={currentStatus === "APPROVED" || selected}
            disabled={!canWrite || busy || currentStatus === "APPROVED" || !selectable}
            onChange={(event) => onToggle(action, event.target.checked)}
          />
          <span className="sr-only">Seleziona azione</span>
        </label>
        <div>
          <strong className="f6">{actionLabel(action)}</strong>
          <div className="f7 scrittaColMedia mt1">
            {approvalTargetLabel(action)}
            {action.language_iso || action.language ? ` - lingua ${action.language_iso || action.language}` : ""}
          </div>
        </div>
        <div className="alignment-action-status">
          <Badge tone={statusTone(currentStatus)}>{currentStatus}</Badge>
          <Badge tone={plannedStatus === "BLOCKED" ? "error" : plannedStatus === "NEEDS_EDITORIAL_REVIEW" ? "warning" : "ready"}>{plannedStatus}</Badge>
        </div>
      </div>

      <div className="flex flex-wrap mt2" style={{ gap: "0.35rem" }}>
        <Badge>{metadata.propagateViaDomino === true ? "Salva in Domino e propaga a VIG" : OPERATION_LABELS[actionOperation(action)] || actionOperation(action) || "Operazione"}</Badge>
        {fallback ? <Badge tone="warning">Fallback IT -&gt; {action.language_iso || action.language || "lingua target"}</Badge> : null}
        {fallback && metadata.fallbackSource ? <Badge>{metadata.fallbackSource}</Badge> : null}
        {method ? <Badge>{method}</Badge> : null}
        {confidence ? <Badge tone={confidence === "MEDIUM" ? "warning" : "muted"}>Confidenza: {confidence}</Badge> : null}
      </div>

      {isImage ? (
        <PrestashopAlignmentImageReview
          itemId={itemId}
          sourceProductId={sourceProductId}
          action={action}
          snapshot={snapshot}
          token={token}
          canWrite={canWrite}
          reviewAllowed={currentStatus === "PROPOSED" && ["READY", "READY_DERIVED"].includes(plannedStatus)}
          pageBusy={busy}
          onRefresh={onRefresh}
          onReconcile={onReconcile}
        />
      ) : null}

      {disabledReason && (currentStatus === "PROPOSED" || isManualIntroAction(action)) ? <p className="f7 alignment-block-reason mb0">{disabledReason}</p> : null}
      {metadata.propagateViaDomino === true ? <p className="f7 lh-copy mb0">Unica approvazione: il dato viene prima salvato in Domino e poi propagato selettivamente a VIG.</p> : null}
      {fallback ? <p className="f7 lh-copy mb0">Fill-only: il valore gia presente sul target non verra sovrascritto.</p> : null}
      {isFeature && featureValues.length ? <p className="f7 lh-copy mb0"><span className="scrittaColMedia">Valore descrittivo Prestashop:</span> <strong>{featureValues.join(" - ")}</strong></p> : null}
      {isCategory ? <CategoryDescriptions site={categorySite} values={action.candidateValue} descriptions={categoryDescriptions} title="Categoria proposta" /> : null}

      {!isImage && (isFeature ? (
        <ActionValue title="Dettaglio tecnico feature" value={{ proposto: action.candidateValue, sorgente: action.sourceValue, target: action.targetValue }} />
      ) : (
        <div className="alignment-value-comparison mt2">
          <div><ActionValue title="Sorgente" value={action.sourceValue} open={derived} collapsible={!isText} /></div>
          <div><ActionValue title="Candidato" value={action.candidateValue} open={derived} collapsible={!isText} /></div>
          <div><ActionValue title="Valore target precedente" value={action.targetValue} open={derived} collapsible={!isText} /></div>
        </div>
      ))}

      {derived && (currentStatus === "PROPOSED" || isManualIntroAction(action)) ? (
        <DerivedTextEditor
          action={action}
          value={draft}
          onChange={(value) => onDraftChange(action, value)}
          onConfirmIntro={(value) => onConfirmIntro(action, value)}
          reviewed={reviewedIds.has(Number(action.id))}
          disabled={!canWrite || busy}
        />
      ) : null}

      <DownstreamState downstream={action.downstream} />
      {currentStatus === "ERROR" && canWrite && !isImage ? (
        <button type="button" className="alignment-button alignment-button--secondary mt2" disabled={busy} onClick={() => onRetry(action.retryAction || action)}>
          Riprova questa azione
        </button>
      ) : null}
    </article>
  );
}

const FINDING_TITLES = {
  SOURCE_FEATURE_UNMAPPED: "Feature sorgente senza mappatura",
  SOURCE_FEATURE_MAPPING_AMBIGUOUS: "Feature sorgente associata a piu campi",
  TARGET_FEATURE_UNMAPPED: "Campo senza feature sul sito di destinazione",
  FEATURE_VALUE_UNRESOLVED: "Valore feature non riconosciuto in Domino",
  FEATURE_DESTINATION_CONFLICT: "Piu feature propongono valori diversi",
  FEATURE_TARGET_CONFLICT: "Il sito di destinazione contiene un valore diverso",
  DOMINO_FEATURE_PATH_UNKNOWN: "Campo feature non presente nello schema Domino",
  DOMINO_FEATURE_CONFLICT: "Domino contiene un valore feature diverso",
  DOMINO_TEXT_CONFLICT: "Domino contiene un testo diverso",
  TARGET_TEXT_CONFLICT: "Il sito di destinazione contiene un testo diverso",
  SOURCE_LANGUAGE_EQUALS_IT: "La lingua contiene una copia del testo italiano",
  MISSING_TRANSLATION_USING_IT_FALLBACK: "Traduzione assente: proposto il testo italiano",
  TEXT_DERIVATION_UNAVAILABLE: "Testo non derivabile automaticamente",
  INTRO_SOURCE_UNSTRUCTURED: "Sezioni utili per la desc intro non riconosciute",
  LONG_TO_SHORT_UNUSABLE: "Descrizione breve non derivabile",
  CATEGORY_MAPPING_INPUT_REQUIRED: "Mancano dati per scegliere la categoria",
  CATEGORY_MAPPING_REVIEW_REQUIRED: "La regola categoria richiede revisione",
  CATEGORY_DEFAULT_AMBIGUOUS: "Categoria predefinita ambigua",
  CATEGORY_DEFAULT_CONFLICT: "Categoria predefinita diversa sul target",
  DOMINO_CATEGORY_CONFLICT: "Categorie Bottero diverse da quelle salvate in Domino",
  DOMINO_CATEGORY_DEFAULT_CONFLICT: "Categoria Bottero predefinita diversa da Domino",
  CATEGORY_TARGET_INVALID: "Categoria di destinazione non valida",
  NATIVE_PARTIAL_RESPONSE: "Prestashop ha restituito dati parziali",
};

function preferredLocalizedValue(rows) {
  const values = Array.isArray(rows) ? rows : [];
  const italian = values.find((row) => String(row?.isoCode || "").toLowerCase() === "it" && row?.value);
  const selected = italian || values.find((row) => row?.value);
  return selected?.value || null;
}

function findingSite(finding, details, fallbackKey) {
  return String(details?.site || finding?.[fallbackKey + "_site"] || finding?.[fallbackKey + "Site"] || "").toUpperCase();
}

function findingFeatureRows(finding, snapshot) {
  const details = findingDetails(finding);
  const sourceSite = findingSite(finding, details, "source");
  const targetSite = findingSite(finding, {}, "target");
  const ids = [details.featureId]
    .concat(Array.isArray(details.conflictingFeatureIds) ? details.conflictingFeatureIds : [])
    .filter((id) => id !== null && typeof id !== "undefined")
    .map(String);
  const path = finding.field_path || finding.fieldPath || details.destinationPath || details.sourcePath;
  const rows = [];
  [sourceSite, targetSite].filter((site, index, sites) => site && sites.indexOf(site) === index).forEach((site) => {
    const features = Array.isArray(snapshot?.sites?.[site]?.features) ? snapshot.sites[site].features : [];
    features.filter((feature) => ids.includes(String(feature?.featureId)) || (!ids.length && path && feature?.mappedPath === path))
      .forEach((feature) => rows.push({
        site,
        id: feature.featureId,
        valueId: feature.featureValueId,
        name: preferredLocalizedValue(feature.names) || feature.mappedPath || details.featureName || null,
        value: preferredLocalizedValue(feature.values),
      }));
  });
  if (!rows.length && (details.featureName || details.featureValue || details.sourceValue || ids.length)) {
    rows.push({
      site: sourceSite || details.site || "--",
      id: details.featureId || ids[0] || null,
      valueId: details.featureValueId || null,
      name: details.featureName || path || null,
      value: details.featureValue ?? details.sourceValue ?? null,
    });
  }
  return rows.filter((row, index) => rows.findIndex((candidate) =>
    candidate.site === row.site && String(candidate.id) === String(row.id) && String(candidate.value) === String(row.value)
  ) === index);
}

function readableFeatureValue(value, fieldPath, descriptions) {
  if (value === null || typeof value === "undefined" || value === "") return null;
  if (Array.isArray(value)) return value.map((entry) => readableFeatureValue(entry, fieldPath, descriptions)).filter(Boolean).join(", ");
  if (typeof value !== "object") return String(value);
  if (value.valueDesc !== null && typeof value.valueDesc !== "undefined") return String(value.valueDesc);
  if (value.valueId !== null && typeof value.valueId !== "undefined") {
    const normalizedPath = String(fieldPath || "").replace(/^datiUlteriori\./, "");
    return descriptions?.[normalizedPath]?.options?.[String(value.valueId)] || "Valore non censito (" + value.valueId + ")";
  }
  return asDisplayValue(value);
}

function FindingFeatureContext({ finding, snapshot, featureDescriptions }) {
  const details = findingDetails(finding);
  const fieldPath = finding.field_path || finding.fieldPath || details.destinationPath || details.sourcePath;
  const rows = findingFeatureRows(finding, snapshot);
  const sourceValue = details.sourceValue ?? details.featureValue;
  const targetValue = details.targetValue;
  const dominoValue = details.dominoValue;
  const schema = featureDescriptions?.[String(fieldPath || "").replace(/^datiUlteriori\./, "")];
  if (!rows.length && sourceValue === undefined && targetValue === undefined && dominoValue === undefined && !schema) return null;
  return (
    <div className="alignment-finding-context mt2">
      {schema ? <div className="f7"><span className="scrittaColMedia">Campo Domino:</span> <strong>{schema.label}</strong></div> : null}
      {rows.length ? <ul className="alignment-feature-list f7 mt2 mb0 pl3">{rows.map((row, index) => (
        <li key={[row.site, row.id, row.valueId, index].join("-")}>
          <strong>{row.name || "Feature " + row.site + (row.id ? " #" + row.id : "")}</strong>
          {row.value !== null && typeof row.value !== "undefined" ? ": " + row.value : ""}
          <span className="scrittaColMedia">{row.site ? " (" + row.site + ")" : ""}</span>
        </li>
      ))}</ul> : null}
      <div className="alignment-finding-values mt2">
        {sourceValue !== undefined ? <FindingValue title="Valore sorgente" value={readableFeatureValue(sourceValue, fieldPath, featureDescriptions)} /> : null}
        {targetValue !== undefined ? <FindingValue title="Valore presente sul target" value={readableFeatureValue(targetValue, fieldPath, featureDescriptions)} /> : null}
        {dominoValue !== undefined ? <FindingValue title="Valore presente in Domino" value={readableFeatureValue(dominoValue, fieldPath, featureDescriptions)} /> : null}
      </div>
    </div>
  );
}

function semanticSiteTexts(snapshot, site) {
  if (site === "DOMINO") return snapshot?.domino?.descrizioniSuPS || {};
  return snapshot?.sites?.[site]?.dominoTexts || {};
}

function sourceTextField(direction, field) {
  if (direction !== "BOT_TO_VIG") return field;
  if (field === "descLunga") return "descIntro";
  if (field === "descBreve") return "descLunga";
  return field;
}

function FindingValue({ title, value }) {
  if (value === null || typeof value === "undefined" || value === "") return null;
  return <div className="alignment-finding-value"><ActionValue title={title} value={value} collapsible={false} /></div>;
}

function FindingTextContext({ finding, snapshot, direction }) {
  const details = findingDetails(finding);
  const fieldPath = finding.field_path || finding.fieldPath || "";
  const field = String(fieldPath).split(".").filter(Boolean).pop();
  const language = String(finding.language_iso || finding.language || finding.details?.language || details.redundantLanguage || "it").toLowerCase();
  const sourceSite = findingSite(finding, details, "source");
  const targetSite = findingSite(finding, {}, "target");
  const sourceField = sourceTextField(direction, field);
  const sourceTexts = semanticSiteTexts(snapshot, sourceSite);
  const targetTexts = semanticSiteTexts(snapshot, targetSite);
  const dominoTexts = semanticSiteTexts(snapshot, "DOMINO");
  const sourceValue = sourceTexts?.[sourceField]?.[language];
  const targetValue = targetTexts?.[field]?.[language];
  const dominoValue = dominoTexts?.[field]?.[language];
  const fallbackItalian = finding.code === "MISSING_TRANSLATION_USING_IT_FALLBACK"
    ? (details.fallbackSource === "TARGET_IT" ? targetTexts?.[field]?.it : sourceTexts?.[sourceField]?.it)
    : null;
  const italianValue = details.sourceLanguage === "it" ? sourceTexts?.[sourceField]?.it : fallbackItalian;
  if (!sourceValue && !targetValue && !dominoValue && !italianValue) return null;
  return (
    <div className="alignment-finding-context mt2">
      <div className="f7"><span className="scrittaColMedia">Campo:</span> <strong>{semanticTextLabel(fieldPath)}</strong> · {LANGUAGE_LABELS[language] || language.toUpperCase()}</div>
      <div className="alignment-finding-values mt2">
        <FindingValue title={"Testo sorgente " + (sourceSite || "")} value={sourceValue} />
        <FindingValue title={finding.code === "MISSING_TRANSLATION_USING_IT_FALLBACK" ? "Testo italiano proposto" : "Testo italiano di confronto"} value={italianValue} />
        <FindingValue title="Testo presente in Domino" value={dominoValue} />
        <FindingValue title={"Testo presente su " + (targetSite || "target")} value={targetValue} />
      </div>
    </div>
  );
}

function categoryFindingGroups(finding) {
  const details = findingDetails(finding);
  const sourceSite = findingSite(finding, {}, "source");
  const targetSite = findingSite(finding, details, "target");
  return [
    { title: "Categorie sorgente", site: sourceSite, values: [details.sourceIds, details.sourceDefaultId] },
    { title: "Categorie salvate in Domino", site: sourceSite, values: [details.dominoIds, details.dominoDefaultId] },
    { title: "Categoria attuale sul target", site: targetSite, values: details.targetDefaultId },
    { title: "Categoria proposta", site: targetSite, values: [details.desiredDefaultId, details.categoryId, details.categoryIds, details.defaultCategoryId] },
  ].filter((group) => categoryIdsFromDisplayValue(group.values).length && ["VIG", "BOT"].includes(group.site));
}

function FindingCategoryContext({ finding, categoryDescriptions }) {
  const groups = categoryFindingGroups(finding);
  if (!groups.length) return null;
  return <div className="alignment-finding-context mt2">{groups.map((group) => (
    <CategoryDescriptions key={group.title + group.site} site={group.site} values={group.values} descriptions={categoryDescriptions} title={group.title} />
  ))}</div>;
}

function FindingFacts({ finding }) {
  const details = findingDetails(finding);
  const facts = [];
  if (details.sourcePath) facts.push(["Feature sorgente", details.sourcePath]);
  if (details.destinationPath) facts.push(["Campo di destinazione", details.destinationPath]);
  if (details.reason) facts.push(["Motivo", details.reason === "NO_MATCH" ? "nessun valore corrispondente" : details.reason === "AMBIGUOUS_MATCH" ? "piu valori corrispondenti" : details.reason]);
  if (details.matchCount !== null && typeof details.matchCount !== "undefined") facts.push(["Corrispondenze trovate", details.matchCount]);
  if (details.message) facts.push(["Messaggio", details.message]);
  if (details.status) facts.push(["Stato risposta", details.status]);
  if (details.sourceLanguage) facts.push(["Lingua sorgente", String(details.sourceLanguage).toUpperCase()]);
  if (details.redundantLanguage) facts.push(["Lingua ridondata", String(details.redundantLanguage).toUpperCase()]);
  const warnings = Array.isArray(details.warnings) ? details.warnings.filter(Boolean) : [];
  if (!facts.length && !warnings.length) return null;
  return (
    <div className="alignment-finding-facts f7 mt2">
      {facts.map(([label, value]) => <div key={label}><span className="scrittaColMedia">{label}:</span> <strong>{String(value)}</strong></div>)}
      {warnings.length ? <div><span className="scrittaColMedia">Avvisi:</span> <strong>{warnings.join(", ")}</strong></div> : null}
    </div>
  );
}

function FindingContext({ finding, snapshot, categoryDescriptions, featureDescriptions, direction }) {
  const domain = String(finding.domain || "").toUpperCase();
  return (
    <>
      <FindingFacts finding={finding} />
      {domain === "FEATURE" ? <FindingFeatureContext finding={finding} snapshot={snapshot} featureDescriptions={featureDescriptions} /> : null}
      {domain === "TEXT" ? <FindingTextContext finding={finding} snapshot={snapshot} direction={direction} /> : null}
      {domain.startsWith("CATEGORY") ? <FindingCategoryContext finding={finding} categoryDescriptions={categoryDescriptions} /> : null}
    </>
  );
}

function MappingRequirements({ finding }) {
  const details = findingDetails(finding);
  const requirements = Array.isArray(details.missingRequirements) && details.missingRequirements.length
    ? details.missingRequirements
    : Array.isArray(details.requirements) ? details.requirements : [];
  if (!requirements.length) return null;
  return (
    <div className="mt2">
      <div className="f7 b">Dati richiesti per una mappatura solida</div>
      <ul className="f7 pl3 mt1 mb0">
        {requirements.map((requirement, index) => (
          <li key={`${requirement.source || "dato"}-${requirement.field || requirement.path || index}`}>
            {requirement.label || requirement.field || requirement.path}
            <span className="scrittaColMedia"> - {requirement.source === "moda" ? "MODA" : "Mongo"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Findings({ findings, snapshot, categoryDescriptions, featureDescriptions, direction }) {
  if (!findings.length) return <p className="ma0 f7 scrittaColMedia">Nessun blocker o finding registrato.</p>;
  return (
    <div>
      {findings.map((finding) => (
        <div key={finding.id || `${finding.code}-${finding.field_path}`} className="pv2 bb bordoColAlt">
          <div className="flex justify-between items-center" style={{ gap: "0.5rem" }}>
            <div><strong className="f6">{FINDING_TITLES[finding.code] || finding.code || "Problema rilevato"}</strong>{FINDING_TITLES[finding.code] ? <div className="f7 scrittaColMedia mt1">{finding.code}</div> : null}</div>
            <Badge tone={finding.severity === "ERROR" || finding.severity === "BLOCKER" ? "error" : "warning"}>{finding.severity || "WARNING"}</Badge>
          </div>
          <div className="f7 scrittaColMedia mt1">{finding.domain || "--"}{finding.field_path ? ` - ${finding.field_path}` : ""}</div>
          <MappingRequirements finding={finding} />
          <FindingContext finding={finding} snapshot={snapshot} categoryDescriptions={categoryDescriptions} featureDescriptions={featureDescriptions} direction={direction} />
          {finding.details ? <ActionValue title="Dettaglio tecnico (ID e hash)" value={finding.details} /> : null}
        </div>
      ))}
    </div>
  );
}

function ConfirmationDialog({ operation, actions, onCancel, onConfirm, busy }) {
  if (!operation) return null;
  const copy = {
    APPROVE: {
      title: "Conferma approvazione selettiva",
      text: "Verranno approvate soltanto le azioni elencate. Le altre proposte e i blocchi resteranno invariati.",
      button: "Conferma approvazione",
    },
    APPLY: {
      title: "Conferma applicazione",
      text: "Domino verra aggiornato per primo; solo dopo verranno create le code Prestashop approvate.",
      button: "Applica le azioni approvate",
    },
    CANCEL: {
      title: "Conferma annullamento",
      text: "L'annullamento - disponibile soltanto prima dell'inizio degli effetti applicativi.",
      button: "Annulla lavorazione",
    },
    REANALYZE: {
      title: "Conferma nuova analisi",
      text: "Verranno riletti Domino, VIG e BOT. Gli esiti gia applicati saranno preservati.",
      button: "Rianalizza articolo",
    },
  }[operation];

  return (
    <div className="alignment-dialog-backdrop" role="presentation">
      <div className="alignment-dialog pa3" role="dialog" aria-modal="true" aria-labelledby="alignment-dialog-title">
        <h2 id="alignment-dialog-title" className="f4 mt0">{copy.title}</h2>
        <p className="f6 lh-copy">{copy.text}</p>
        {operation === "APPROVE" ? (
          <ul className="alignment-confirm-list f7 pl3">
            {actions.map((action) => <li key={action.id}>{actionLabel(action)} - {approvalTargetLabel(action)}{action.language_iso ? " - " + action.language_iso : ""}</li>)}
          </ul>
        ) : null}
        <div className="flex justify-end mt3" style={{ gap: "0.5rem" }}>
          <button type="button" className="alignment-button alignment-button--secondary" onClick={onCancel} disabled={busy}>Torna alla revisione</button>
          <button type="button" className="alignment-button alignment-button--primary" onClick={onConfirm} disabled={busy}>{busy ? "Operazione in corso&" : copy.button}</button>
        </div>
      </div>
    </div>
  );
}

const BULK_ACCEPTANCE_CONFIRMATION = "ACCETTA TUTTA LA RUN";

function BulkAcceptanceDialog({ run, onCancel, onConfirm, busy }) {
  const [confirmationText, setConfirmationText] = useState("");
  if (!run) return null;
  const readyItems = Number(run.ready_items || 0);
  const canConfirm = confirmationText.trim().toUpperCase() === BULK_ACCEPTANCE_CONFIRMATION;
  return (
    <div className="alignment-dialog-backdrop" role="presentation">
      <div className="alignment-dialog pa3" role="dialog" aria-modal="true" aria-labelledby="alignment-bulk-dialog-title">
        <h2 id="alignment-bulk-dialog-title" className="f4 mt0">Accettazione massiva della run</h2>
        <p className="f6 lh-copy">
          Verranno accettati tutti i dati e tutte le immagini applicabili dei <strong>{readyItems}</strong> articoli
          pronti o pronti con dati parziali della run <strong>{run.run_id}</strong>. Gli articoli bloccati resteranno da gestire.
        </p>
        <p className="alignment-bulk-warning f6 lh-copy">
          Questa operazione viene registrata in Mongo come “accettazione massiva e sconsiderata” e non richiede una revisione articolo per articolo.
        </p>
        <label className="db f7 b" htmlFor="alignment-bulk-confirmation">
          Per confermare scrivi {BULK_ACCEPTANCE_CONFIRMATION}
        </label>
        <input
          id="alignment-bulk-confirmation"
          className={`${stilePulsanti.inputClasses} w-100 mt2`}
          value={confirmationText}
          onChange={(event) => setConfirmationText(event.target.value)}
          autoComplete="off"
        />
        <div className="flex justify-end mt3" style={{ gap: "0.5rem" }}>
          <button type="button" className="alignment-button alignment-button--secondary" onClick={onCancel} disabled={busy}>Annulla</button>
          <button type="button" className="alignment-button alignment-button--danger" onClick={onConfirm} disabled={busy || !canConfirm}>
            {busy ? "Accodamento in corso…" : "Accetta tutta la run"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewSidebar({
  item,
  actions,
  selectedIds,
  drafts,
  reviewedIds,
  canWrite,
  busy,
  onBack,
  onSelectAll,
  onConfirm,
  onReconcile,
  progressByTab,
}) {
  const approved = actions.filter((action) => actionStatus(action) === "APPROVED");
  const started = actions.some((action) => ["APPLYING", "APPLIED"].includes(actionStatus(action)) || action.downstream);
  const selectedActions = actions.filter((action) => selectedIds.has(Number(action.id)));
  const selectableIds = new Set(selectableReviewActionIds(actions, drafts, reviewedIds));
  const selectableActions = actions.filter((action) => selectableIds.has(Number(action.id)));
  const allSelectableSelected = selectableActions.length > 0 &&
    selectableActions.every((action) => selectedIds.has(Number(action.id)));
  const canReanalyze = !actions.some((action) => actionStatus(action) === "APPLYING");
  return (
    <aside className="alignment-review-sidebar" aria-label="Azioni revisione articolo">
      {onBack ? (
        <button type="button" className="alignment-sidebar-back" onClick={onBack}>
          <span aria-hidden="true">←</span> Torna all'elenco
        </button>
      ) : null}

      <div className="alignment-sidebar-header">
        <span className="alignment-sidebar-eyebrow">Revisione allineamento</span>
        <h1>{item.codVS || "Articolo"}</h1>
        <p>{item.direction || "--"}</p>
        <p>Analizzato {formatDate(item.analyzed_at || item.updated_at)}</p>
        <div className="alignment-sidebar-badges mt2">
          <Badge tone={statusTone(item.status)}>{alignmentStatusLabel(item.status)}</Badge>
          <Badge tone={canWrite ? "ready" : "warning"}>{canWrite ? "Modalita operativa" : "Solo lettura"}</Badge>
        </div>
      </div>

      <div className="alignment-sidebar-progress" aria-label="Conteggi e avanzamento per sezione">
        <span className="alignment-sidebar-section-label">Voci e avanzamento</span>
        {REVIEW_TABS.map((tab) => (
          <div className="alignment-sidebar-progress-row" key={tab.id}>
            <span>{tab.label}</span>
            <ProgressIndicator progress={progressByTab[tab.id]} tracksChoices={tab.tracksChoices} compact />
          </div>
        ))}
      </div>

      <div className="alignment-sidebar-selection">
        <strong>{selectedIds.size} {selectedIds.size === 1 ? "azione selezionata" : "azioni selezionate"}</strong>
        <span>{actions.length - selectedIds.size} non selezionate o non modificabili</span>
      </div>

      <div className="alignment-sidebar-actions">
        {!canWrite ? <Badge tone="warning">Permessi update:prestashop e write:debug richiesti</Badge> : null}
        <button
          type="button"
          className="alignment-button alignment-button--sidebar"
          disabled={!canWrite || busy || !selectableActions.length || allSelectableSelected}
          onClick={() => onSelectAll(selectableActions)}
        >
          Seleziona tutto
        </button>
        <button type="button" className="alignment-button alignment-button--primary" disabled={!canWrite || busy || !selectedActions.length} onClick={() => onConfirm("APPROVE")}>Rivedi e approva</button>
        <button type="button" className="alignment-button alignment-button--primary" disabled={!canWrite || busy || !approved.length} onClick={() => onConfirm("APPLY")}>Applica approvate</button>
        <button type="button" className="alignment-button alignment-button--sidebar" disabled={!canWrite || busy || started} onClick={() => onConfirm("CANCEL")}>Annulla</button>
        <button type="button" className="alignment-button alignment-button--sidebar" disabled={!canWrite || busy || !canReanalyze} onClick={() => onConfirm("REANALYZE")}>Rianalizza</button>
        <button type="button" className="alignment-button alignment-button--sidebar" disabled={!canWrite || busy} onClick={onReconcile}>Aggiorna stato</button>
      </div>
    </aside>
  );
}

const CATEGORY_CALCULATION_FIELDS = [
  ["reparto", "Reparto"],
  ["categoria", "Categoria"],
  ["tipo", "Tipo"],
  ["sottotipo", "Sottotipo"],
  ["persona", "Persona"],
];

function nestedValue(source, path) {
  return String(path || "").split(".").filter(Boolean).reduce((value, key) => (
    value && typeof value === "object" ? value[key] : undefined
  ), source);
}

function conditionActualValue(condition, snapshot) {
  return condition?.source === "mongo"
    ? nestedValue(snapshot?.domino, condition.path || condition.field)
    : snapshot?.moda?.[condition.field || condition.path];
}

function conditionDescription(condition, snapshot) {
  const field = condition?.field || condition?.path || "campo";
  const operator = condition?.operator || "eq";
  const expected = condition?.value ?? condition?.values;
  const actual = conditionActualValue(condition, snapshot);
  return field + " " + operator + " " + asDisplayValue(expected) + " (articolo: " + asDisplayValue(actual) + ")";
}

function CategoryCalculationPanel({ item, snapshot, token, canWrite, busy, categoryDescriptions, onRefresh, onSelectionChanged }) {
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [selectionError, setSelectionError] = useState("");
  const site = String(item?.target_site || (item?.direction === "BOT_TO_VIG" ? "VIG" : "BOT")).toUpperCase();
  const resolution = snapshot?.categoryResolution?.[site];
  if (!resolution) return null;
  const mappings = Array.isArray(resolution.compatibleMappings)
    ? resolution.compatibleMappings
    : Array.isArray(resolution.mappings) ? resolution.mappings : [];
  const selectedRuleId = Number(resolution.selectedRuleId || snapshot?.categoryMappingSelections?.[site]?.ruleId) || null;
  const compatibleRuleIds = Array.from(new Set(mappings.map((mapping) => Number(mapping?.ruleId)).filter(Number.isFinite)));
  const selectedRule = (resolution.appliedRules || []).find((rule) => Number(rule?.ruleId) === selectedRuleId);
  const displayedRules = (resolution.appliedRules || []).filter((rule) =>
    compatibleRuleIds.includes(Number(rule?.ruleId)) && (!selectedRuleId || Number(rule?.ruleId) === selectedRuleId),
  );
  const selection = resolution.mappingSelection || snapshot?.categoryMappingSelections?.[site] || null;
  const locked = ["APPROVED", "APPLYING", "WAITING_DOWNSTREAM", "SUCCESS", "PARTIAL_SUCCESS"].includes(String(item?.status || "").toUpperCase());

  const selectMapping = async (ruleId) => {
    if (!canWrite || busy || saving) return;
    setSaving(true);
    setMessage("");
    setSelectionError("");
    try {
      await axios.post(
        apiUrl + "/moda/prestashop-alignment/items/" + item.id + "/category-mapping-selection",
        { site, ruleId },
        { headers: { Authorization: "Bearer " + token } },
      );
      onSelectionChanged();
      await onRefresh();
      setMessage(ruleId === null ? "Ripristinato il calcolo automatico." : "Mappatura scelta e proposte categoria ricalcolate.");
    } catch (requestError) {
      setSelectionError(requestErrorMessage(requestError, "Impossibile cambiare la mappatura categoria."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="alignment-category-calculation" aria-labelledby="alignment-category-calculation-title">
      <div className="alignment-category-calculation__heading">
        <div>
          <span className="alignment-tab-kicker">Provenienza della proposta</span>
          <h3 id="alignment-category-calculation-title">Come è stata calcolata</h3>
        </div>
        <Badge tone={selectedRuleId ? "ready" : compatibleRuleIds.length > 1 ? "warning" : "muted"}>
          {selectedRuleId ? "Scelta manuale" : "Calcolo automatico"}
        </Badge>
      </div>
      <p className="f6 lh-copy mt0">
        Le regole attive sono confrontate con i dati MODA e Domino dell’articolo. Sono risultate compatibili <strong>{compatibleRuleIds.length}</strong> mappature per {site}; le categorie proposte derivano {selectedRuleId ? "solo dalla regola scelta" : "dall’insieme delle regole compatibili"}.
      </p>
      <p className="f7 lh-copy scrittaColMedia">Le categorie online della sorgente restano evidenza di confronto: il resolver non presume che gli ID BOT e VIG coincidano e calcola il target dalle regole MODA/Mongo.</p>

      <div className="alignment-category-calculation__grid">
        <div>
          <h4 className="f6 mt0 mb2">Dati usati dal resolver</h4>
          <dl className="alignment-category-inputs">
            {CATEGORY_CALCULATION_FIELDS.map(([field, label]) => (
              <div key={field}><dt>{label}</dt><dd>{asDisplayValue(snapshot?.moda?.[field])}</dd></div>
            ))}
          </dl>
          <CategoryDescriptions
            site={site}
            values={[resolution.categoryIds, resolution.defaultCategoryId]}
            descriptions={categoryDescriptions}
            title="Risultato complessivo calcolato"
          />
          <p className="f7 lh-copy scrittaColMedia mt1">Le azioni sottostanti mostrano separatamente soltanto le categorie del risultato calcolato che devono ancora essere associate al sito.</p>
          {displayedRules.length ? (
            <div className="mt3">
              <strong className="f7">{selectedRule ? "Condizioni della regola scelta" : "Condizioni delle regole compatibili"}</strong>
              {displayedRules.map((rule) => (
                <div key={rule.ruleId} className="mt2">
                  <div className="f7 fw6">{rule.ruleDescription || rule.ruleKey || "Regola #" + rule.ruleId}</div>
                  <ul className="f7 lh-copy mt1 mb0">
                    {(rule.conditions || []).map((condition, index) => (
                      <li key={String(rule.ruleId) + "-" + index}>{conditionDescription(condition, snapshot)}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}
          {selection ? <p className="f7 scrittaColMedia mb0">Scelta da {selection.selectedBy || "utente non disponibile"}{selection.selectedAt ? " il " + formatDate(selection.selectedAt) : ""}.</p> : null}
        </div>

        <div>
          <div className="flex justify-between items-center mb2" style={{ gap: "0.75rem" }}>
            <h4 className="f6 ma0">Mappature compatibili</h4>
            {selectedRuleId ? (
              <button type="button" className="alignment-button alignment-button--secondary" disabled={!canWrite || busy || saving || locked} onClick={() => selectMapping(null)}>
                Ripristina automatico
              </button>
            ) : null}
          </div>
          <WebCategoryMappingCards
            mappings={mappings}
            rowKey={"alignment-" + item.id + "-" + site}
            site={site}
            selectedRuleId={selectedRuleId}
            onSelect={selectMapping}
            disabled={!canWrite || busy || saving || locked}
          />
          {locked ? <p className="f7 alignment-block-reason">La scelta non è modificabile dopo l’approvazione.</p> : null}
          {selectionError ? <p className="f7 dark-red" role="alert">{selectionError}</p> : null}
          {message ? <p className="f7 dark-green" role="status">{message}</p> : null}
        </div>
      </div>
    </section>
  );
}

function AlignmentInspector({
  item,
  loading,
  token,
  onBack,
  selectedIds,
  drafts,
  reviewedIds,
  canWrite,
  busy,
  onToggle,
  onSelectAll,
  onDraftChange,
  onConfirmIntro,
  onConfirm,
  onReconcile,
  onRefresh,
  onRetry,
  embedded = false,
}) {
  const [activeTab, setActiveTab] = useState("summary");

  useEffect(() => {
    setActiveTab("summary");
  }, [item?.id]);

  if (loading) return <DetailSkeleton />;
  if (!item) return <EmptyInspector />;
  const actions = logicalReviewActions(item.actions);
  const findings = Array.isArray(item.findings) ? item.findings : [];
  const textInformationFindings = findings.filter(isInformationalTextFinding);
  const problemFindings = findings.filter((finding) => !isInformationalTextFinding(finding));
  const snapshot = item.snapshot || {};
  const progressByTab = REVIEW_TABS.reduce((result, tab) => {
    result[tab.id] = reviewProgress(tab.id, actions, problemFindings, selectedIds);
    return result;
  }, {});
  const activeConfig = REVIEW_TABS.find((tab) => tab.id === activeTab) || REVIEW_TABS[0];
  const activeActions = actions.filter((action) => reviewTabForAction(action) === activeTab);

  const renderAction = (action) => (
    <ActionRow
      key={action.id || action.actionKey}
      action={action}
      snapshot={snapshot}
      categoryDescriptions={item.categoryDescriptions}
      selected={selectedIds.has(Number(action.id))}
      drafts={drafts}
      reviewedIds={reviewedIds}
      canWrite={canWrite}
      busy={busy}
      onToggle={onToggle}
      onDraftChange={onDraftChange}
      onConfirmIntro={onConfirmIntro}
      onRetry={onRetry}
      itemId={item.id}
      sourceProductId={item.source_product_id}
      token={token}
      onRefresh={onRefresh}
      onReconcile={onReconcile}
    />
  );
  const actionRows = activeActions.length ? activeActions.map(renderAction) : (
    <div className="alignment-tab-empty">Nessuna voce in questa sezione.</div>
  );

  return (
    <div className={"alignment-review-workspace" + (embedded ? " alignment-review-workspace--embedded" : "")}>
      <ReviewSidebar
        item={item}
        actions={actions}
        selectedIds={selectedIds}
        canWrite={canWrite}
        busy={busy}
        drafts={drafts}
        reviewedIds={reviewedIds}
        onBack={onBack}
        onSelectAll={onSelectAll}
        onConfirm={onConfirm}
        onReconcile={onReconcile}
        progressByTab={progressByTab}
      />

      <section className="alignment-review-main">
        <nav className="alignment-review-tabs" role="tablist" aria-label="Sezioni revisione allineamento">
          {REVIEW_TABS.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                type="button"
                role="tab"
                id={"alignment-tab-" + tab.id}
                aria-controls={"alignment-panel-" + tab.id}
                aria-selected={active}
                className={"alignment-review-tab" + (active ? " alignment-review-tab--active" : "")}
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                <span>{tab.label}</span>
                <ProgressIndicator progress={progressByTab[tab.id]} tracksChoices={tab.tracksChoices} compact />
              </button>
            );
          })}
        </nav>

        <div
          className="alignment-review-tabpanel"
          role="tabpanel"
          id={"alignment-panel-" + activeTab}
          aria-labelledby={"alignment-tab-" + activeTab}
          tabIndex="0"
        >
          <div className="alignment-tab-heading">
            <div>
              <span className="alignment-tab-kicker">{activeConfig.label}</span>
              <h2>{activeTab === "summary" ? "Confronto delle fonti" : activeTab === "problems" ? "Blocker e mappature mancanti" : activeConfig.label}</h2>
            </div>
            <ProgressIndicator progress={progressByTab[activeTab]} tracksChoices={activeConfig.tracksChoices} />
          </div>

          {activeTab === "summary" ? (
            <div className="alignment-comparison">
              <SourceComparisonPanel snapshot={snapshot} sourceName="DOMINO" productId={item.domino_product_id || snapshot.domino?._id || snapshot.domino?.id} categoryDescriptions={item.categoryDescriptions} />
              <SourceComparisonPanel snapshot={snapshot} sourceName="VIG" productId={snapshot.sites?.VIG?.productId || (item.source_site === "VIG" ? item.source_product_id : item.target_product_id)} categoryDescriptions={item.categoryDescriptions} />
              <SourceComparisonPanel snapshot={snapshot} sourceName="BOT" productId={snapshot.sites?.BOT?.productId || (item.source_site === "BOT" ? item.source_product_id : item.target_product_id)} categoryDescriptions={item.categoryDescriptions} />
            </div>
          ) : activeTab === "texts" ? (
            <TextReviewSections actions={activeActions} findings={textInformationFindings} renderAction={renderAction} />
          ) : activeTab === "categories" ? (
            <div>
              <CategoryCalculationPanel
                item={item}
                snapshot={snapshot}
                token={token}
                canWrite={canWrite}
                busy={busy}
                categoryDescriptions={item.categoryDescriptions}
                onRefresh={onRefresh}
                onSelectionChanged={() => actions.filter((action) => ["CATEGORY", "CATEGORY_DEFAULT"].includes(String(action.domain || "").toUpperCase())).forEach((action) => onToggle(action, false))}
              />
              <div className="alignment-tab-actions">{actionRows}</div>
            </div>
          ) : activeTab === "problems" ? (
            <div className="alignment-problems-panel">
              <Findings findings={problemFindings} snapshot={snapshot} categoryDescriptions={item.categoryDescriptions} featureDescriptions={item.featureDescriptions} direction={item.direction} />
              {activeActions.length ? <div className="alignment-tab-actions mt3">{actionRows}</div> : null}
            </div>
          ) : (
            <div className="alignment-tab-actions">{actionRows}</div>
          )}
        </div>
      </section>
    </div>
  );
}

function requestErrorMessage(error, fallback) {
  const code = error?.response?.data?.code;
  const message = error?.response?.data?.error || error?.message || fallback;
  return code ? `${message} (${code})` : message;
}

function PrestashopAlignmentPage({ token, utente }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState("");
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [drafts, setDrafts] = useState({});
  const [reviewedIds, setReviewedIds] = useState(() => new Set());
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [bulkAcceptanceRun, setBulkAcceptanceRun] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [directionFilter, setDirectionFilter] = useState("ALL");
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  const selectedItemIdRef = useRef(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const requestedItemId = searchParams.get("itemId");
  const canWrite = Boolean(
    utente?.permessiAPI?.["update:prestashop"] &&
    utente?.permessiAPI?.["write:debug"],
  );

  const loadDetail = useCallback(async (itemId, options = {}) => {
    if (!token || !itemId) return null;
    if (!options.silent) {
      setLoadingDetail(true);
      setError("");
    }
    try {
      const response = await axios.get(`${apiUrl}/moda/prestashop-alignment/items/${itemId}`, { headers: authHeaders });
      const detail = response?.data?.data || null;
      setSelectedItem(detail);
      if (detail?.run_id) setRunId(String(detail.run_id));
      return detail;
    } catch (requestError) {
      if (!options.silent) setSelectedItem(null);
      setError(requestErrorMessage(requestError, "Impossibile caricare il dettaglio dell'articolo."));
      return null;
    } finally {
      if (!options.silent) setLoadingDetail(false);
    }
  }, [authHeaders, token]);

  const loadRuns = useCallback(async (options = {}) => {
    if (!token) return;
    if (!options.silent) {
      setLoadingRuns(true);
      setError("");
    }
    try {
      const response = await axios.get(`${apiUrl}/moda/prestashop-alignment/runs`, { headers: authHeaders, params: { limit: 50, mode: "BATCH" } });
      const list = Array.isArray(response?.data?.data) ? response.data.data : [];
      setRuns(list);
      setRunId((current) => current || (list[0]?.run_id ? String(list[0].run_id) : ""));
    } catch (requestError) {
      setRuns([]);
      setError(requestErrorMessage(requestError, "Impossibile caricare le run di analisi."));
    } finally {
      if (!options.silent) setLoadingRuns(false);
    }
  }, [authHeaders, token]);

  const loadItems = useCallback(async (options = {}) => {
    if (!token || !runId) return;
    if (!options.silent) {
      setLoadingItems(true);
      setError("");
    }
    try {
      const response = await axios.get(`${apiUrl}/moda/prestashop-alignment/runs/${encodeURIComponent(runId)}/items`, { headers: authHeaders, params: { limit: 500 } });
      setItems(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (requestError) {
      setItems([]);
      setError(requestErrorMessage(requestError, "Impossibile caricare gli articoli della run."));
    } finally {
      if (!options.silent) setLoadingItems(false);
    }
  }, [authHeaders, runId, token]);

  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { if (requestedItemId) loadDetail(requestedItemId); }, [loadDetail, requestedItemId]);

  const selectedRun = useMemo(() => runs.find((run) => String(run.run_id) === String(runId)) || null, [runId, runs]);
  useEffect(() => {
    if (!selectedRun || !["PENDING", "RUNNING"].includes(selectedRun.status)) return undefined;
    const interval = window.setInterval(() => {
      void loadRuns({ silent: true });
      void loadItems({ silent: true });
      setDashboardRefreshKey((current) => current + 1);
    }, 3000);
    return () => window.clearInterval(interval);
  }, [loadItems, loadRuns, selectedRun]);

  const selectedItemActions = selectedItem?.actions;

  useEffect(() => {
    const actions = Array.isArray(selectedItemActions) ? selectedItemActions : [];
    const currentItemId = selectedItem?.id ?? null;
    const sameItem = currentItemId !== null &&
      String(selectedItemIdRef.current) === String(currentItemId);
    setDrafts((current) => {
      const next = {};
      actions.filter(isDerivedText).forEach((action) => {
        const key = String(action.id);
        next[key] = sameItem && Object.prototype.hasOwnProperty.call(current, key)
          ? current[key]
          : candidateString(action);
      });
      return next;
    });
    setSelectedIds((current) => {
      if (!sameItem) return new Set();
      const proposedIds = new Set(
        logicalReviewActions(actions)
          .filter((action) => actionStatus(action) === "PROPOSED" || isManualIntroAction(action))
          .map((action) => Number(action.id)),
      );
      return new Set(Array.from(current).filter((id) => proposedIds.has(Number(id))));
    });
    setReviewedIds((current) => {
      if (!sameItem) return new Set();
      const proposedIds = new Set(actions.filter((action) => actionStatus(action) === "PROPOSED" || isManualIntroAction(action)).map((action) => Number(action.id)));
      return new Set(Array.from(current).filter((id) => proposedIds.has(id)));
    });
    selectedItemIdRef.current = currentItemId;
  }, [selectedItem?.id, selectedItemActions]);

  const reconcile = useCallback(async (silent = false) => {
    if (!selectedItem?.id || !canWrite || busy) return;
    if (!silent) {
      setBusy(true);
      setError("");
      setNotice("");
    }
    try {
      await axios.post(
        `${apiUrl}/moda/prestashop-alignment/items/${selectedItem.id}/reconcile`,
        {},
        { headers: authHeaders },
      );
      await loadDetail(selectedItem.id, { silent: true });
      if (!silent) setNotice("Stato downstream aggiornato.");
    } catch (requestError) {
      if (!silent) setError(requestErrorMessage(requestError, "Impossibile aggiornare lo stato downstream."));
    } finally {
      if (!silent) setBusy(false);
    }
  }, [authHeaders, busy, canWrite, loadDetail, selectedItem?.id]);

  useEffect(() => {
    if (!canWrite || !selectedItem?.id || !["APPLYING", "WAITING_DOWNSTREAM"].includes(selectedItem.status)) return undefined;
    const interval = window.setInterval(() => reconcile(true), 12000);
    return () => window.clearInterval(interval);
  }, [canWrite, reconcile, selectedItem?.id, selectedItem?.status]);

  const visibleItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return items.filter((item) => {
      if (statusFilter !== "ALL" && item.status !== statusFilter) return false;
      if (directionFilter !== "ALL" && item.direction !== directionFilter) return false;
      if (!normalizedSearch) return true;
      return [item.codVS, item.source_product_id, item.target_product_id, item.domino_product_id]
        .some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
    });
  }, [directionFilter, items, search, statusFilter]);

  const selectItem = (item) => setSearchParams({ itemId: String(item.id) });
  const closeReview = () => {
    setSelectedItem(null);
    setSelectedIds(new Set());
    setDrafts({});
    setReviewedIds(new Set());
    setRunId((current) => runs.some((run) => String(run.run_id) === String(current))
      ? current
      : (runs[0]?.run_id ? String(runs[0].run_id) : ""));
    setSearchParams({});
  };
  const toggleAction = (action, checked) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(Number(action.id));
      else next.delete(Number(action.id));
      return next;
    });
  };
  const selectAllActions = (actions) => {
    setSelectedIds(new Set(actions.map((action) => Number(action.id)).filter(Number.isFinite)));
  };
  const changeDraft = (action, value) => {
    setDrafts((current) => ({ ...current, [String(action.id)]: value }));
    if (plannedActionStatus(action) === "NEEDS_EDITORIAL_REVIEW" && !candidateChanged(action, value)) {
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(Number(action.id));
        return next;
      });
    }
  };
  const confirmIntro = (action, value) => {
    setDrafts((current) => ({ ...current, [String(action.id)]: value }));
    setReviewedIds((current) => new Set(current).add(Number(action.id)));
    setSelectedIds((current) => new Set(current).add(Number(action.id)));
  };

  const selectedActions = logicalReviewActions(selectedItem?.actions).filter((action) => selectedIds.has(Number(action.id)));

  const runConfirmedOperation = async () => {
    if (!confirmation || !selectedItem?.id) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (confirmation === "APPROVE") {
        const candidateEdits = selectedActions
          .filter((action) => isDerivedText(action) && (reviewedIds.has(Number(action.id)) || candidateChanged(action, drafts[String(action.id)])))
          .map((action) => ({ actionId: Number(action.id), candidateValue: drafts[String(action.id)] }));
        await axios.post(
          `${apiUrl}/moda/prestashop-alignment/items/${selectedItem.id}/approve`,
          { actionIds: selectedActions.map((action) => Number(action.id)), candidateEdits },
          { headers: authHeaders },
        );
        setNotice("Azioni approvate. Verifica il riepilogo e applicale quando sei pronto.");
      } else if (confirmation === "APPLY") {
        await axios.post(`${apiUrl}/moda/prestashop-alignment/items/${selectedItem.id}/apply`, {}, { headers: authHeaders });
        setNotice("Applicazione avviata. Lo stato downstream verra aggiornato automaticamente.");
      } else if (confirmation === "CANCEL") {
        await axios.post(`${apiUrl}/moda/prestashop-alignment/items/${selectedItem.id}/cancel`, {}, { headers: authHeaders });
        setNotice("Lavorazione annullata.");
      } else if (confirmation === "REANALYZE") {
        await axios.post(`${apiUrl}/moda/prestashop-alignment/items/${selectedItem.id}/reanalyze`, {}, { headers: authHeaders });
        setSelectedIds(new Set());
        setDrafts({});
        setReviewedIds(new Set());
        setNotice("Articolo rianalizzato con i dati correnti.");
      }
      setConfirmation(null);
      await loadDetail(selectedItem.id, { silent: true });
      await loadItems();
    } catch (requestError) {
      setConfirmation(null);
      setError(requestErrorMessage(requestError, "Operazione non completata."));
      await loadDetail(selectedItem.id, { silent: true });
    } finally {
      setBusy(false);
    }
  };

  const retryAction = async (action) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await axios.post(`${apiUrl}/moda/prestashop-alignment/actions/${action.id}/retry`, {}, { headers: authHeaders });
      setNotice("Retry avviato per l'azione selezionata.");
      await loadDetail(selectedItem.id, { silent: true });
    } catch (requestError) {
      setError(requestErrorMessage(requestError, "Retry non completato."));
    } finally {
      setBusy(false);
    }
  };

  const refreshSelectedDetail = useCallback(async () => {
    if (!selectedItem?.id) return null;
    return loadDetail(selectedItem.id, { silent: true });
  }, [loadDetail, selectedItem?.id]);

  const batchCreated = useCallback(async (data) => {
    if (!data?.runId) return;
    setRunId(String(data.runId));
    setNotice(data.workerEnabled
      ? "Batch preview accodato. La lista si aggiornera durante l analisi."
      : "Batch salvato ma worker sospeso: verificare VSTRANSLATIONAPI_ENABLE_CRON_IMPORT.");
    await loadRuns({ silent: true });
  }, [loadRuns]);

  const blockedItemsReanalyzed = useCallback(async () => {
    await Promise.all([
      loadRuns({ silent: true }),
      loadItems({ silent: true }),
    ]);
    setDashboardRefreshKey((current) => current + 1);
  }, [loadItems, loadRuns]);

  const reconcileEntireRun = useCallback(async () => {
    if (!runId || !utente?.username) return;
    setBusy(true);
    setError("");
    setNotice("");
    let cursor = 0;
    let reconciled = 0;
    const failures = [];
    try {
      for (;;) {
        const response = await axios.post(
          apiUrl + "/moda/prestashop-alignment/runs/" + encodeURIComponent(runId) + "/reconcile",
          { afterItemId: cursor, limit: 25, username: utente.username },
          { headers: authHeaders },
        );
        const data = response?.data?.data || {};
        reconciled += Number(data.reconciledItems || 0);
        failures.push(...(data.outcomes || []).filter((row) => row.error));
        if (data.nextCursor === null || data.nextCursor === undefined) break;
        if (Number(data.nextCursor) <= cursor) throw new Error("Cursore di riconciliazione non avanzato.");
        cursor = Number(data.nextCursor);
      }
      setNotice(
        "Stati riconciliati per " + reconciled + " articoli." +
        (failures.length ? " Errori su " + failures.length + " item: " +
          failures.slice(0, 5).map((row) => row.itemId).join(", ") + "." : ""),
      );
      await Promise.all([loadRuns({ silent: true }), loadItems({ silent: true })]);
      setDashboardRefreshKey((current) => current + 1);
    } catch (requestError) {
      setError(requestErrorMessage(requestError, "Riconciliazione della run non completata."));
    } finally {
      setBusy(false);
    }
  }, [authHeaders, loadItems, loadRuns, runId, utente?.username]);

  const acceptEntireRun = useCallback(async () => {
    if (!bulkAcceptanceRun?.run_id || !utente?.username) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const response = await axios.post(
        `${apiUrl}/moda/prestashop-alignment/runs/${encodeURIComponent(bulkAcceptanceRun.run_id)}/accept-all`,
        { username: utente.username },
        { headers: authHeaders },
      );
      const data = response?.data?.data || {};
      setNotice(
        data.workerEnabled === false
          ? `Accettazione massiva salvata per ${data.totalItems || 0} articoli, ma il worker e sospeso: verificare VSTRANSLATIONAPI_ENABLE_CRON_IMPORT.`
          : data.alreadyQueued
          ? "L'accettazione massiva era gia in coda: il worker proseguira dal checkpoint salvato."
          : `Accettazione massiva accodata per ${data.totalItems || 0} articoli. I bloccati restano in elenco.`,
      );
      setBulkAcceptanceRun(null);
      await Promise.all([loadRuns({ silent: true }), loadItems({ silent: true })]);
      setDashboardRefreshKey((current) => current + 1);
    } catch (requestError) {
      setError(requestErrorMessage(requestError, "Impossibile accodare l'accettazione massiva."));
    } finally {
      setBusy(false);
    }
  }, [authHeaders, bulkAcceptanceRun, loadItems, loadRuns, utente?.username]);

  if (requestedItemId) return (
    <main className="alignment-review-page">
      {(error || notice) ? (
        <div className="alignment-review-messages">
          {error ? <div role="alert" className="pa3 sfondoColAltScuro scrittaColChiara">{error}</div> : null}
          {notice ? <div role="status" className="alignment-notice pa3">{notice}</div> : null}
        </div>
      ) : null}
      <AlignmentInspector
        item={selectedItem}
        loading={loadingDetail}
        token={token}
        onBack={closeReview}
        selectedIds={selectedIds}
        drafts={drafts}
        reviewedIds={reviewedIds}
        canWrite={canWrite}
        busy={busy}
        onToggle={toggleAction}
        onSelectAll={selectAllActions}
        onDraftChange={changeDraft}
        onConfirmIntro={confirmIntro}
        onConfirm={setConfirmation}
        onReconcile={(silent) => reconcile(Boolean(silent))}
        onRefresh={refreshSelectedDetail}
        onRetry={retryAction}
      />
      <ConfirmationDialog
        operation={confirmation}
        actions={selectedActions}
        onCancel={() => setConfirmation(null)}
        onConfirm={runConfirmedOperation}
        busy={busy}
      />
    </main>
  );

  return (
    <main className="alignment-page pa3">
      <header className="alignment-page-header mb3">
        <div className="flex justify-between items-start" style={{ gap: "1rem" }}>
          <div>
            <h1 className="f3 ma0">Allineamento VIG/BOT</h1>
            <p className="mt2 mb0 f6 scrittaColMedia">Scegli un articolo per confrontare e revisionare le azioni proposte.</p>
          </div>
          <Badge tone={canWrite ? "ready" : "warning"}>{canWrite ? "Revisione manuale" : "Solo lettura"}</Badge>
        </div>
      </header>

      <PrestashopAlignmentBatchPreview token={token} canWrite={canWrite} onCreated={batchCreated} />
      <PrestashopAlignmentDashboard
        token={token}
        runId={runId}
        canWrite={canWrite}
        refreshKey={dashboardRefreshKey}
        onReanalyzed={blockedItemsReanalyzed}
      />
      {notice ? <div role="status" className="alignment-notice pa3 mb3">{notice}</div> : null}
      {selectedRun?.mode === "BATCH" ? (
        <section className="alignment-run-progress pa3 mb3 sfondoBiancoTema bordoColAlt ba" aria-label="Stato batch selezionato">
          <div><strong>{alignmentStatusLabel(selectedRun.status)}</strong><span className="f7 scrittaColMedia">{selectedRun.last_step || "Run batch"}</span></div>
          <div><span>Totale <strong>{selectedRun.total_items || 0}</strong></span><span>Pronti <strong>{selectedRun.ready_items || 0}</strong></span><span>Bloccati <strong>{selectedRun.blocked_items || 0}</strong></span><span>Errori <strong>{selectedRun.error_items || 0}</strong></span></div>
          <div className="alignment-run-bulk-actions">
            <button
              type="button"
              className="alignment-button alignment-button--danger"
              onClick={() => setBulkAcceptanceRun(selectedRun)}
              disabled={busy || !canWrite || !utente?.username || Number(selectedRun.ready_items || 0) < 1 || ["PENDING", "RUNNING"].includes(String(selectedRun.config?.bulkAcceptance?.status || ""))}
              title={!utente?.username ? "Username Domino mancante per l'audit" : undefined}
            >
              Accetta tutti i pronti della run
            </button>
            <button
              type="button"
              className="alignment-button"
              onClick={reconcileEntireRun}
              disabled={busy || !canWrite || !utente?.username}
              title="Legge gli esiti già conclusi nelle dashboard dati e immagini; non invia nuovamente il prodotto"
            >
              {busy ? "Riconciliazione in corso…" : "Aggiorna esiti della run"}
            </button>
            {selectedRun.config?.bulkAcceptance ? (
              <span className="f7 scrittaColMedia">
                Massiva: {selectedRun.config.bulkAcceptance.nextIndex || 0}/{selectedRun.config.bulkAcceptance.itemIds?.length || 0}
                {selectedRun.config.bulkAcceptance.lastMessage ? ` — ${selectedRun.config.bulkAcceptance.lastMessage}` : ""}
              </span>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="alignment-toolbar pa3 sfondoColScuro" aria-label="Filtri allineamento">
        <label className="f7"><span className="db mb1">Run</span><select className={stilePulsanti.inputClasses} value={runId} onChange={(event) => { setSelectedItem(null); setRunId(event.target.value); }} disabled={loadingRuns}><option value="">Nessuna run disponibile</option>{runs.map((run) => <option key={run.run_id} value={run.run_id}>{run.run_id} - {run.direction} - {alignmentStatusLabel(run.status)} - {formatDate(run.updated_at)}</option>)}</select></label>
        <label className="f7"><span className="db mb1">Cerca articolo</span><input className={stilePulsanti.inputClasses} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="codVS o ID" /></label>
        <label className="f7"><span className="db mb1">Direzione</span><select className={stilePulsanti.inputClasses} value={directionFilter} onChange={(event) => setDirectionFilter(event.target.value)}><option value="ALL">Tutte</option><option value="VIG_TO_BOT">VIG - BOT</option><option value="BOT_TO_VIG">BOT - VIG</option></select></label>
        <label className="f7"><span className="db mb1">Stato</span><select className={stilePulsanti.inputClasses} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="ALL">Tutti</option>{Object.keys({ PENDING: 1, ANALYZING: 1, READY: 1, PARTIAL_READY: 1, APPROVED: 1, WAITING_DOWNSTREAM: 1, SUCCESS: 1, PARTIAL_SUCCESS: 1, BLOCKED: 1, STALE: 1, ERROR: 1, CANCELLED: 1, NO_ACTIONS: 1 }).map((status) => <option key={status} value={status}>{alignmentStatusLabel(status)}</option>)}</select></label>
      </section>

      {error ? <div role="alert" className="pa3 mb3 sfondoColAltScuro scrittaColChiara">{error}</div> : null}

      <section className="alignment-workspace sfondoBiancoTema br3 pa3">
        <div className="alignment-list overflow-auto">
          <div className="pa3 bb bordoColAlt flex justify-between items-center"><strong>Articoli</strong><span className="f7 scrittaColMedia">{visibleItems.length} di {items.length}</span></div>
          {loadingItems ? <TableSkeleton /> : visibleItems.length ? <table className="collapse w-100 f7" style={{ minWidth: "1180px" }}><thead><tr className="bb bordoColAlt tl"><th className="pa2">Articolo</th><th className="pa2">Direzione</th><th className="pa2">Stato</th><th className="pa2">Categorie di partenza</th><th className="pa2">Categorie proposte</th><th className="pa2">Diff</th><th className="pa2">Ultima analisi</th></tr></thead><tbody>{visibleItems.map((item) => { const counts = countActionsByDomain(item.analysis?.actions); return <tr key={item.id} className="alignment-row" onClick={() => selectItem(item)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") selectItem(item); }} tabIndex={0} aria-label={`Apri revisione ${item.codVS || item.id}`}><td className="pa2 bb bordoColAlt"><strong>{item.codVS || "--"}</strong><div className="scrittaColMedia mt1">VIG {item.source_site === "VIG" ? item.source_product_id || "--" : item.target_product_id || "--"} - BOT {item.source_site === "BOT" ? item.source_product_id || "--" : item.target_product_id || "--"}</div></td><td className="pa2 bb bordoColAlt">{item.direction === "VIG_TO_BOT" ? "VIG -> BOT" : "BOT -> VIG"}</td><td className="pa2 bb bordoColAlt"><Badge tone={statusTone(item.status)}>{alignmentStatusLabel(item.status)}</Badge></td><td className="pa2 bb bordoColAlt"><CategoryListPreview value={item.categoryPreview?.source} /></td><td className="pa2 bb bordoColAlt"><CategoryListPreview value={item.categoryPreview?.proposed} /></td><td className="pa2 bb bordoColAlt">{Object.entries(counts).map(([domain, count]) => <div key={domain}>{DOMAIN_LABELS[domain] || domain}: {count}</div>)}</td><td className="pa2 bb bordoColAlt">{formatDate(item.analyzed_at || item.updated_at)}</td></tr>; })}</tbody></table> : <div className="pa4 tc scrittaColMedia">Nessun articolo corrisponde ai filtri di questa run.</div>}
        </div>
      </section>
      <BulkAcceptanceDialog
        run={bulkAcceptanceRun}
        onCancel={() => setBulkAcceptanceRun(null)}
        onConfirm={acceptEntireRun}
        busy={busy}
      />
    </main>
  );
}

export { AlignmentInspector, ConfirmationDialog, BulkAcceptanceDialog, requestErrorMessage, logicalReviewActions, isDerivedText, candidateChanged, candidateString, actionStatus };
export { buildAlignmentItemHref, alignmentStatusLabel, semanticTextLabel, siteState } from "./PrestashopAlignmentPage.helpers";
export default PrestashopAlignmentPage;
